import { createOperationId } from "./operationId";
export type ComponentStatus =
  | "healthy"
  | "degraded"
  | "unavailable"
  | "disabled"
  | "unknown";

export type AdminUserState = "active" | "disabled";

export interface AdminUser {
  admin_user_id: string;
  username: string;
  display_name: string;
  state: AdminUserState;
  must_change_password: boolean;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
}

export interface AuthSession {
  user: AdminUser;
  csrf_token: string;
  expires_at: string;
}

export interface ProcessSnapshot {
  state: string;
  live: boolean;
  ready: boolean;
  started_at: string;
  state_changed_at: string;
  reason?: string;
}

export interface StatusCounts {
  total: number;
  healthy: number;
  degraded: number;
  unavailable: number;
  disabled: number;
  unknown: number;
}

export interface Overview {
  data_center_id: string;
  service: string;
  release: string;
  observed_at: string;
  process: ProcessSnapshot;
  components: StatusCounts;
}

export interface ComponentObservation {
  id: string;
  kind: string;
  name: string;
  status: ComponentStatus;
  observed_at: string;
  error_code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

export interface ComponentsResponse {
  data_center_id: string;
  observed_at: string;
  items: ComponentObservation[];
}

export interface DashboardData {
  overview: Overview;
  components: ComponentsResponse;
}

export type MonitoringStatus = "healthy" | "degraded" | "critical" | "unknown";

export interface MonitoringOverview {
	data_center_id: string;
	observed_at: string;
	enabled: boolean;
	status: MonitoringStatus;
	metrics: {
		available: boolean;
		data_freshness_seconds: number | null;
		collector_targets: number | null;
		healthy_collector_targets: number | null;
		max_export_queue: number | null;
		export_failures_15m: number | null;
		go_request_rate: number | null;
		python_request_rate: number | null;
		go_request_p95_seconds: number | null;
		python_request_p95_seconds: number | null;
		missing: string[];
	};
	alerts: { available: boolean; firing: number; critical: number; warning: number; suppressed: number };
	explorer_url?: string;
	warnings: string[];
}

export interface MonitoringAlert {
	fingerprint: string;
	state: string;
	labels: Record<string, string>;
	annotations: Record<string, string>;
	starts_at: string;
	ends_at: string;
	updated_at: string;
}

export interface MonitoringRule {
	name: string;
	state: string;
	health: string;
	query: string;
	duration: string;
	last_error?: string;
	labels: Record<string, string>;
	annotations: Record<string, string>;
}

export interface MonitoringSilence {
	silence_id: string;
	state: string;
	matchers: Array<{ name: string; value: string; is_regex: boolean; is_equal: boolean }>;
	starts_at: string;
	ends_at: string;
	created_by: string;
	comment: string;
	updated_at: string;
}

export interface MonitoringAlertsSnapshot {
	data_center_id: string;
	observed_at: string;
	available: boolean;
	alerts_available: boolean;
	rules_available: boolean;
	silences_available: boolean;
	items: MonitoringAlert[];
	rules: MonitoringRule[];
	silences: MonitoringSilence[];
	warning?: string;
}

export interface IDSDirectoryPolicy {
	registration_limit_count: number;
	membership_limit_count: number;
	route_manifest_ttl_seconds: number;
	revision: number;
	updated_by: string;
	created_at: string;
	updated_at: string;
}

export interface IDSDataCenter {
 static_origin?: string;
 bridge_origin?: string;
	data_center_id: string;
	public_origin: string;
	display_name: string;
	lifecycle: "provisioning" | "active" | "draining" | "disabled";
	capabilities: string[];
	credential_audience: string;
	trust_bundle_revision: number;
	ingress_ready: boolean;
	revision: number;
	created_at: string;
	updated_at: string;
}

export interface IDSConfiguration {
	directory_policy: IDSDirectoryPolicy;
	data_centers: IDSDataCenter[];
}

export interface IDSTeam {
	team_id: string;
	display_name: string;
	canonical_slug: string;
	lifecycle: "provisioning" | "active" | "disabled" | "tombstoned";
	home_data_center_id: string;
	route_revision: number;
	owner_user_id: string;
	created_at: string;
	updated_at: string;
}

export type IDSTeamSort = "team_id" | "display_name" | "home_data_center_id" | "lifecycle" | "owner_user_id" | "route_revision" | "created_at" | "updated_at";
export type SortDirection = "asc" | "desc";

export interface IDSTeamQuery {
	page: number;
	page_size: number;
	search?: string;
	data_center_id?: string;
	lifecycle?: IDSTeam["lifecycle"];
	sort: IDSTeamSort;
	direction: SortDirection;
}

export interface IDSTeamPage {
	items: IDSTeam[];
	total: number;
	page: number;
	page_size: number;
}

export interface IDSTeamRegistration {
	operation_id: string;
	user_id: string;
	team_id: string;
	data_center_id: string;
	state: "requested" | "running" | "ready" | "failed";
	step: "reserved" | "database" | "schema" | "owner_membership" | "binding" | "placement" | "ready";
	attempt_count: number;
	revision: number;
	lease_holder?: string;
	lease_until?: string;
	error_code?: string;
	error_message?: string;
	created_at: string;
	updated_at: string;
	completed_at?: string;
}

export interface IDSOperation {
	diagnostic?: import("./diagnosticMessage").Diagnostic;
	operation_id: string;
	component_id: string;
	action: string;
	target_id: string;
	requested_by_admin_user_id: string;
	reason: string;
	state: "pending" | "succeeded" | "failed";
	error_code?: string;
	message?: string;
	created_at: string;
	completed_at?: string;
}

export interface IDSDirectoryPolicyInput {
	expected_revision: number;
	membership_limit_count: number;
	route_manifest_ttl_seconds: number;
}

export interface IDSDataCenterInput {
 static_origin?: string;
 bridge_origin?: string;
	expected_revision: number;
	public_origin: string;
	display_name: string;
	lifecycle: IDSDataCenter["lifecycle"];
	capabilities: string[];
	credential_audience: string;
	trust_bundle_revision: number;
	ingress_ready: boolean;
}

export interface PactureAPIInstance {
	admission_policy?: {revision:number;accepting:boolean};
	capacity_policy?: { revision: number; max_team_slots: number; applied_revision: number; last_error?: string };
	instance_id: string;
	data_center_id: string;
	endpoint: string;
	release_version: string;
	supported_schema_version: string;
	lifecycle: "ready" | "draining" | "unavailable";
	max_team_slots: number;
	loaded_team_count: number;
	db_connection_budget: number;
	db_connections_used: number;
	heartbeat_at: string;
	revision: number;
}

export interface TeamPlacementRecord {
	team_id: string;
	data_center_id: string;
	lifecycle: string;
	placement_state: string;
	owner_instance_id: string;
	owner_endpoint: string;
	placement_epoch: number;
	owner_lease_until: string;
	route_revision: number;
	pacture_api_release: string;
	web_release: string;
	compatibility_revision: number;
	record_revision: number;
	database_binding_ref: string;
	schema_version: string;
}

export interface ManagedTeamPlacement {
	placement: TeamPlacementRecord;
	owner_instance: PactureAPIInstance;
	operation_lease?: {
		team_id: string;
		holder_id: string;
		lease_until: string;
		revision: number;
	};
	owner_heartbeat_fresh: boolean;
	owner_lease_fresh: boolean;
	route_eligible: boolean;
}

export interface TeamRuntimeSnapshot {
	moves?: TeamMove[];
	upgrade_coordination_supported?: boolean;
	active_upgrade?: {
		revision: number;
		operation_id: string;
		state: "stopping" | "stopped" | "resuming" | "ready" | "complete" | "superseded";
		plan: { api_release: string; web_release: string; schema_version: string; spec_sha256: string; reason: string; requested_by: string };
		team_count: number;
		stopped: number;
		restored: number;
		error_code?: string;
	};
	data_center_id: string;
	observed_at: string;
	instances: PactureAPIInstance[];
	placements: ManagedTeamPlacement[];
}

export interface TeamMove {
	operation_id:string;team_id:string;state:string;revision:number;error_code?:string;
	request:{target_instance_id:string;expected_owner_id:string};
	source:TeamPlacementRecord;
}

export function setInstanceAdmission(csrfToken:string,reason:string,instance:PactureAPIInstance,accepting:boolean){
	return request<IDSOperation>(`/api/v1/team-runtime/instances/${encodeURIComponent(instance.instance_id)}/admission`,{method:"PUT",csrfToken,reason,body:{expected_revision:instance.admission_policy?.revision??0,accepting}});
}
export function moveTeam(csrfToken:string,reason:string,source:TeamPlacementRecord,targetInstanceID:string){
	return request<IDSOperation>(`/api/v1/team-runtime/${encodeURIComponent(source.team_id)}/move`,{method:"POST",csrfToken,reason,body:{expected_owner_id:source.owner_instance_id,expected_epoch:source.placement_epoch,expected_route_revision:source.route_revision,target_instance_id:targetInstanceID}});
}

export interface TeamPlacementResult {
	team_id: string;
	action: string;
	owner_instance_id: string;
	placement_epoch: number;
	route_revision: number;
	record_revision: number;
	cleanup_pending: boolean;
}

export interface SandboxFleetTeam {
	team_id: string;
	pod_count: number;
	allocated_cpu_milli: number;
	allocated_mem_mib: number;
	storage_gib: number;
	cost: number;
}

export interface SandboxRuntime {
 status: "active" | "reclaiming";
	sandbox_id: string;
	workspace_id: string;
	team_id: string;
	resource_class: string;
	project_ref?: string;
	scope: string;
	pod_name: string;
	namespace: string;
	created_at: string;
	last_used_at?: string;
	keep_alive_until?: string;
	idle_seconds: number;
	age_seconds: number;
	pinned: boolean;
}

export interface SandboxRuntimeTask {
	task_id: string;
	role: string;
	task_type: string;
	status: string;
	created_at: string;
	updated_at: string;
}

export interface SandboxRuntimeSnapshot {
	observed_at: string;
	fleet: {
		currency: string;
		teams: SandboxFleetTeam[];
		totals: {
			pod_count: number;
			team_count: number;
			allocated_cpu_milli: number;
			allocated_mem_mib: number;
			storage_gib: number;
			cost: number;
		};
	};
	metrics: { available: boolean; sample_namespaces: string[]; pods: Record<string, unknown> };
	runtimes: SandboxRuntime[];
	reclaim_policy: {
		enabled: boolean;
		conversation_idle_ttl_seconds: number;
		project_idle_ttl_seconds: number;
		max_lifetime_seconds: number;
		work_area_ttl_days: number;
		configured: boolean;
	};
	reclaim_log: Array<{
		at: string;
		actor: string;
		sandbox_id: string;
		workspace_id: string;
		scope: string;
		reason: string;
		pod_name: string;
	}>;
}

export type WorkspaceStorageBackend = "local_nfs" | "aws_efs";

export interface WorkspaceStorageConfiguration {
	configured: boolean;
	enabled: boolean;
	backend: WorkspaceStorageBackend;
	local: { server: string; export_path: string; root_path: string };
	aws: {
		region: string;
		eks_cluster: string;
		environment: string;
		efs_name_prefix: string;
		subnet_ids: string[];
		source_security_group_ids: string[];
		efs_security_group_id: string;
		account_id: string;
		posix_uid: number;
		posix_gid: number;
		credential_source?: string;
	};
	directory_mode: string;
	init_timeout_seconds: number;
	updated_by?: string;
}

export type WorkspaceStorageConfigurationInput = Omit<WorkspaceStorageConfiguration, "configured" | "updated_by">;

export interface WorkspaceStorageProject {
	team_id: string;
	project_ref: string;
	backend: WorkspaceStorageBackend;
	status: string;
	storage_ref: string;
	mount_path: string;
	source_sub_path: string;
}

export interface WorkspaceStorageTeam {
	team_id: string;
	backend: WorkspaceStorageBackend;
	status: string;
	storage_ref: string;
	server: string;
	export_path: string;
	team_root_path: string;
	projects: WorkspaceStorageProject[];
}

export interface WorkspaceStorageSnapshot {
	observed_at: string;
	configuration: WorkspaceStorageConfiguration;
	systems: { teams: WorkspaceStorageTeam[] };
}

export interface LLMGatewayProvider {
	id: string;
	model_name: string;
	upstream_model: string;
	api_base: string;
	api_path: string;
	api_compat_mode: "openai" | "anthropic";
	has_key: boolean;
	display_name: string;
	context_window: number | null;
	max_output_tokens: number | null;
	// Optional USD-per-million-token decimal strings; null/absent means no explicit price.
	input_cost_per_million_usd?: string | null;
	output_cost_per_million_usd?: string | null;
	cache_read_cost_per_million_usd?: string | null;
	reasoning_supported: boolean | null;
	reasoning_effort: string;
	enabled: boolean;
	is_default: boolean;
	remote_model_catalog: LLMGatewayRemoteModel[];
}

export type LLMGatewayCapabilitySource = "provider" | "openrouter_reference" | "manual";

export interface LLMGatewayModelCapabilities {
	context_length: number | null;
	max_completion_tokens: number | null;
	supported_parameters: string[] | null;
	reasoning: {
		supported: boolean | null;
		mandatory: boolean | null;
		default_enabled: boolean | null;
		supported_efforts: string[] | null;
		default_effort: string | null;
	};
	architecture: {
		input_modalities: string[];
		output_modalities: string[];
	};
}

export interface LLMGatewayRemoteModel {
	id: string;
	custom: boolean;
	capabilities: LLMGatewayModelCapabilities;
	options: { reasoning_effort: string | null; reasoning_summary?: "concise" | null };
	capability_sources: Record<string, LLMGatewayCapabilitySource>;
	capability_references: {
		openrouter: { model_id: string; capabilities: LLMGatewayModelCapabilities } | null;
	};
}

export interface LLMGatewayProviderInput {
	model_ref: string;
	upstream_model: string;
	api_base: string;
	api_path: string;
	api_compat_mode: "openai" | "anthropic";
	api_key: string;
	display_name: string;
	context_window: number | null;
	max_output_tokens: number | null;
	// Optional USD-per-million-token decimal strings; omit the key entirely when unset.
	input_cost_per_million_usd?: string | null;
	output_cost_per_million_usd?: string | null;
	cache_read_cost_per_million_usd?: string | null;
	reasoning_supported: boolean | null;
	reasoning_effort: string;
	enabled: boolean;
	is_default: boolean;
	remote_model_catalog: LLMGatewayRemoteModel[];
}

export interface LLMGatewayProviderDiscoveryInput {
	api_base: string;
	api_path: string;
	api_compat_mode: "openai" | "anthropic";
	api_key: string;
	model_ref: string;
}

export interface LLMGatewayProviderDiscoveryResult {
	items: LLMGatewayRemoteModel[];
	endpoint?: string;
	capability_reference_endpoint?: string;
	capability_reference_error?: string | null;
}

export interface LLMGatewayProviderTestInput extends LLMGatewayProviderDiscoveryInput {
	remote_model: string;
}

export interface LLMGatewayAttachmentParserModels {
	image: string;
	audio: string;
	video: string;
}

export interface AdminUserInput {
  username: string;
  display_name: string;
  password: string;
}

export class APIError extends Error {
	readonly diagnostic: import("./diagnosticMessage").Diagnostic;
  readonly status: number;
  readonly code: string;
  readonly requestId: string;

  constructor(status: number, code: string, message: string, diagnostic?: import("./diagnosticMessage").Diagnostic, requestId = "") {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
	this.diagnostic=diagnostic??{code};
  }
}

const configuredBase = (import.meta.env.VITE_CONTROL_CENTER_API_BASE as string | undefined)?.trim();
const apiBase = configuredBase ? configuredBase.replace(/\/$/, "") : "";

export async function getSetupState(signal?: AbortSignal): Promise<boolean> {
  const response = await request<{ setup_required: boolean }>("/api/v1/auth/setup", { signal });
  return response.setup_required;
}

export function bootstrapAdministrator(
  bootstrapToken: string,
  input: AdminUserInput
): Promise<AuthSession> {
  return request<AuthSession>("/api/v1/auth/bootstrap", {
    method: "POST",
    body: { bootstrap_token: bootstrapToken, ...input }
  });
}

export function loginAdministrator(username: string, password: string): Promise<AuthSession> {
  return request<AuthSession>("/api/v1/auth/login", {
    method: "POST",
    body: { username, password }
  });
}

export function getSession(signal?: AbortSignal): Promise<AuthSession> {
  return request<AuthSession>("/api/v1/auth/session", { signal });
}

export function logoutAdministrator(csrfToken: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>("/api/v1/auth/logout", { method: "POST", csrfToken });
}

export function changeOwnPassword(
  csrfToken: string,
  currentPassword: string,
  newPassword: string
): Promise<AuthSession> {
  return request<AuthSession>("/api/v1/auth/change-password", {
    method: "POST",
    csrfToken,
    body: { current_password: currentPassword, new_password: newPassword }
  });
}

export async function loadDashboard(signal?: AbortSignal): Promise<DashboardData> {
  const [overview, components] = await Promise.all([
    request<Overview>("/api/v1/overview", { signal }),
    request<ComponentsResponse>("/api/v1/components", { signal })
  ]);
  return { overview, components };
}

export function getMonitoringOverview(signal?: AbortSignal): Promise<MonitoringOverview> {
	return request<MonitoringOverview>("/api/v1/monitoring/overview", { signal });
}

export function getMonitoringAlerts(signal?: AbortSignal): Promise<MonitoringAlertsSnapshot> {
	return request<MonitoringAlertsSnapshot>("/api/v1/monitoring/alerts", { signal });
}

export function createMonitoringSilence(
	csrfToken: string,
	reason: string,
	alertName: string,
	durationSeconds: number
): Promise<MonitoringSilence> {
	return request<MonitoringSilence>("/api/v1/monitoring/silences", {
		method: "POST",
		csrfToken,
		reason,
		body: { alert_name: alertName, duration_seconds: durationSeconds }
	});
}

export function expireMonitoringSilence(csrfToken: string, reason: string, silenceID: string): Promise<{ expired: boolean }> {
	return request<{ expired: boolean }>(`/api/v1/monitoring/silences/${encodeURIComponent(silenceID)}`, {
		method: "DELETE",
		csrfToken,
		reason
	});
}

export async function listAdminUsers(): Promise<AdminUser[]> {
  const response = await request<{ items: AdminUser[] }>("/api/v1/admin-users");
  return response.items;
}

export function createAdminUser(csrfToken: string, input: AdminUserInput): Promise<AdminUser> {
  return request<AdminUser>("/api/v1/admin-users", {
    method: "POST",
    csrfToken,
    body: input
  });
}

export function setAdminUserState(
  csrfToken: string,
  userID: string,
  state: AdminUserState
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/v1/admin-users/${encodeURIComponent(userID)}/state`, {
    method: "POST",
    csrfToken,
    body: { state }
  });
}

export function resetAdminUserPassword(
  csrfToken: string,
  userID: string,
  password: string
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`/api/v1/admin-users/${encodeURIComponent(userID)}/password`, {
    method: "POST",
    csrfToken,
    body: { password }
  });
}

export function getIDSConfiguration(): Promise<IDSConfiguration> {
	return request<IDSConfiguration>("/api/v1/ids/configuration");
}

export function listIDSTeams(query: IDSTeamQuery): Promise<IDSTeamPage> {
	const search = new URLSearchParams({
		page: String(query.page),
		page_size: String(query.page_size),
		sort: query.sort,
		direction: query.direction
	});
	if (query.search) search.set("search", query.search);
	if (query.data_center_id) search.set("data_center_id", query.data_center_id);
	if (query.lifecycle) search.set("lifecycle", query.lifecycle);
	return request<IDSTeamPage>(`/api/v1/ids/teams?${search.toString()}`);
}

export async function listIDSTeamRegistrations(): Promise<IDSTeamRegistration[]> {
	const response = await request<{ items: IDSTeamRegistration[] }>("/api/v1/ids/team-registrations");
	return response.items;
}

export function retryIDSTeamRegistration(
	csrfToken: string,
	reason: string,
	operationID: string,
	expectedRevision: number
): Promise<IDSTeamRegistration> {
	return request<IDSTeamRegistration>(`/api/v1/ids/team-registrations/${encodeURIComponent(operationID)}/retry`, {
		method: "POST",
		csrfToken,
		reason,
		body: { expected_revision: expectedRevision }
	});
}

export async function listIDSOperations(): Promise<IDSOperation[]> {
	const response = await request<{ items: IDSOperation[] }>("/api/v1/ids/operations");
	return response.items;
}

export function updateIDSDirectoryPolicy(
	csrfToken: string,
	reason: string,
	input: IDSDirectoryPolicyInput
): Promise<IDSDirectoryPolicy> {
	return request<IDSDirectoryPolicy>("/api/v1/ids/configuration/directory-policy", {
		method: "POST",
		csrfToken,
		reason,
		body: input
	});
}

export function upsertIDSDataCenter(
	csrfToken: string,
	reason: string,
	input: IDSDataCenterInput
): Promise<IDSDataCenter> {
	return request<IDSDataCenter>("/api/v1/ids/configuration/data-center", {
		method: "POST",
		csrfToken,
		reason,
		body: input
	});
}

export function getTeamRuntime(): Promise<TeamRuntimeSnapshot> {
	return request<TeamRuntimeSnapshot>("/api/v1/team-runtime");
}

export function setInstanceCapacity(csrfToken: string, reason: string, instance: PactureAPIInstance, maxTeamSlots: number) {
	return request<IDSOperation>(`/api/v1/team-runtime/instances/${encodeURIComponent(instance.instance_id)}/capacity`, {
		method: "PUT", csrfToken, reason,
		body: { expected_revision: instance.capacity_policy?.revision ?? 0, max_team_slots: maxTeamSlots }
	});
}

export function continueMaintenance(csrfToken: string, reason: string, operation: NonNullable<TeamRuntimeSnapshot["active_upgrade"]>, action: "drain"|"resume"|"complete", acceptanceConfirmed: boolean) {
	return request<IDSOperation>(`/api/v1/team-runtime/upgrades/${encodeURIComponent(operation.operation_id)}/continue`, {
		method: "POST", csrfToken, reason,
		body: { expected_revision: operation.revision, spec_sha256: operation.plan.spec_sha256, action, acceptance_confirmed: acceptanceConfirmed }
	});
}

export async function listTeamRuntimeOperations(): Promise<IDSOperation[]> {
	const response = await request<{ items: IDSOperation[] }>("/api/v1/team-runtime/operations");
	return response.items;
}

export function reconcileTeamRuntime(
	csrfToken: string,
	reason: string,
	teamID: string,
	expectedRecordRevision: number
): Promise<TeamPlacementResult> {
	return request<TeamPlacementResult>(`/api/v1/team-runtime/${encodeURIComponent(teamID)}/reconcile`, {
		method: "POST",
		csrfToken,
		reason,
		body: { expected_record_revision: expectedRecordRevision }
	});
}

export function getSandboxRuntime(): Promise<SandboxRuntimeSnapshot> {
	return request<SandboxRuntimeSnapshot>("/api/v1/sandbox/runtime");
}

export function getWorkspaceStorage(): Promise<WorkspaceStorageSnapshot> {
	return request<WorkspaceStorageSnapshot>("/api/v1/sandbox/workspace-storage");
}

export function updateWorkspaceStorageConfiguration(
	csrfToken: string,
	reason: string,
	input: WorkspaceStorageConfigurationInput
): Promise<WorkspaceStorageConfiguration> {
	return request<WorkspaceStorageConfiguration>("/api/v1/sandbox/workspace-storage/configuration", {
		method: "PUT",
		csrfToken,
		reason,
		body: input
	});
}

export async function listSandboxRuntimeTasks(sandboxID: string): Promise<SandboxRuntimeTask[]> {
	const response = await request<{ items: SandboxRuntimeTask[] }>(`/api/v1/sandbox/runtime/${encodeURIComponent(sandboxID)}/tasks`);
	return response.items;
}

export async function listSandboxOperations(): Promise<IDSOperation[]> {
	const response = await request<{ items: IDSOperation[] }>("/api/v1/sandbox/operations");
	return response.items;
}

type RuntimeReclaimOutcome = {
 operation: { state: string };
 result?: { error_code: string; deletion_confirmed: boolean };
};
export async function reclaimSandboxRuntime(csrfToken: string, reason: string, sandboxID: string): Promise<RuntimeReclaimOutcome> {
 const assessment = await request<{ pod_uid: string | null }>(`/api/v1/sandbox/runtime/${encodeURIComponent(sandboxID)}/reclaim-assessment`);
 const commandID = createOperationId();
 let outcome = await request<RuntimeReclaimOutcome>(`/api/v1/sandbox/runtime/${encodeURIComponent(sandboxID)}/reclaim`, {
  method: "POST", csrfToken, reason,
  body: { command_id: commandID, expected_pod_uid: assessment.pod_uid }
 });
 for (let attempt = 0; attempt < 12 && outcome.operation.state === "pending"; attempt++) {
  await new Promise(resolve => setTimeout(resolve, 1000));
  outcome = await request<RuntimeReclaimOutcome>(`/api/v1/sandbox/runtime-reclaim/commands/${encodeURIComponent(commandID)}`);
 }
 return outcome;
}

export type RuntimeRetentionOutcome = {
 command_id?: string;
 operation: { operation_id: string; state: string };
 result?: { error_code: string; keep_alive_until: string | null };
};
export async function keepAliveSandboxRuntime(csrfToken: string, reason: string, sandboxID: string, seconds: number | null): Promise<RuntimeRetentionOutcome> {
 const assessment = await request<{ pod_uid: string | null; keep_alive_until: string | null }>(`/api/v1/sandbox/runtime/${encodeURIComponent(sandboxID)}/reclaim-assessment`);
 const commandID = createOperationId();
 const body = { command_id: commandID, sandbox_id: sandboxID, reason,
  expected_pod_uid: assessment.pod_uid, expected_keep_alive_until: assessment.keep_alive_until,
  keep_alive_until: seconds === null ? null : new Date(Math.floor(Date.now()/1000)*1000 + seconds*1000).toISOString().replace(".000Z", "Z") };
 let outcome: RuntimeRetentionOutcome;
 try {
  outcome = await request<RuntimeRetentionOutcome>("/api/v1/sandbox/runtime-retention/commands", {method: "POST", csrfToken, reason, body});
 } catch (error) {
  if (error instanceof APIError && error.status < 500) throw error;
  // Never create a new intent after a lost response; recover the original command.
  try { outcome = await request<RuntimeRetentionOutcome>(`/api/v1/sandbox/runtime-retention/commands/${encodeURIComponent(commandID)}`); }
  catch { outcome = {command_id:commandID, operation:{operation_id:"",state:"pending"}}; }
 }
 for (let attempt=0; attempt<3 && outcome.operation.state === "pending"; attempt++) {
  await new Promise(resolve => setTimeout(resolve,1000));
  try { outcome = await request<RuntimeRetentionOutcome>("/api/v1/sandbox/runtime-retention/commands", {method: "POST", csrfToken, reason, body}); }
  catch { break; }
 }
 return {...outcome, command_id:commandID};
}

export async function listLLMGatewayProviders(): Promise<LLMGatewayProvider[]> {
	const response = await request<{ providers: LLMGatewayProvider[] }>("/api/v1/llm-gateway/providers");
	return response.providers;
}

export async function listLLMGatewayOperations(): Promise<IDSOperation[]> {
	const response = await request<{ items: IDSOperation[] }>("/api/v1/llm-gateway/operations");
	return response.items;
}

export async function discoverLLMGatewayModels(
	csrfToken: string,
	input: LLMGatewayProviderDiscoveryInput
): Promise<LLMGatewayProviderDiscoveryResult> {
	return request<LLMGatewayProviderDiscoveryResult>("/api/v1/llm-gateway/providers/discover", {
		method: "POST", csrfToken, body: input
	});
}

export function testLLMGatewayProvider(
	csrfToken: string,
	input: LLMGatewayProviderTestInput
): Promise<{ ok: boolean; model: string; endpoint?: string }> {
	return request<{ ok: boolean; model: string; endpoint?: string }>("/api/v1/llm-gateway/providers/test", {
		method: "POST", csrfToken, body: input
	});
}

export async function getLLMGatewayAttachmentParsers(): Promise<LLMGatewayAttachmentParserModels> {
	const response = await request<{ models?: Partial<LLMGatewayAttachmentParserModels> }>("/api/v1/llm-gateway/attachment-parsers");
	return {
		image: response.models?.image ?? "",
		audio: response.models?.audio ?? "",
		video: response.models?.video ?? ""
	};
}

export async function updateLLMGatewayAttachmentParsers(
	csrfToken: string,
	reason: string,
	models: LLMGatewayAttachmentParserModels
): Promise<LLMGatewayAttachmentParserModels> {
	const response = await request<{ models?: Partial<LLMGatewayAttachmentParserModels> }>("/api/v1/llm-gateway/attachment-parsers", {
		method: "PUT", csrfToken, reason, body: { models }
	});
	return {
		image: response.models?.image ?? "",
		audio: response.models?.audio ?? "",
		video: response.models?.video ?? ""
	};
}

export function upsertLLMGatewayProvider(
	csrfToken: string,
	reason: string,
	input: LLMGatewayProviderInput
): Promise<LLMGatewayProvider> {
	// The owner persists only this provider's selected model; discovery catalogs
	// stay in the picker and must not inflate a single-provider write beyond 64 KiB.
	const selected = input.remote_model_catalog.find((model) => model.id.trim() === input.upstream_model.trim());
	return request<LLMGatewayProvider>("/api/v1/llm-gateway/providers", {
		method: "PUT", csrfToken, reason,
		body: { ...input, remote_model_catalog: selected ? [selected] : [] }
	});
}

export function deleteLLMGatewayProvider(
	csrfToken: string,
	reason: string,
	modelID: string
): Promise<{ deleted: string }> {
	return request<{ deleted: string }>(`/api/v1/llm-gateway/providers/${encodeURIComponent(modelID)}`, {
		method: "DELETE", csrfToken, reason
	});
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  csrfToken?: string;
	reason?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.csrfToken) headers["X-Control-Center-CSRF"] = options.csrfToken;
	if (options.reason) {
    // 浏览器 Header 不能直接承载中文；显式标记编码以兼容原有 ASCII 调用方。
    headers["X-Control-Center-Reason"] = encodeURIComponent(options.reason);
    headers["X-Control-Center-Reason-Encoding"] = "uri";
  }
  const response = await fetch(`${apiBase}${path}`, {
    method: options.method ?? "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    cache: "no-store",
    credentials: "same-origin",
    signal: options.signal
  });
  if (!response.ok) {
	let diagnostic: import("./diagnosticMessage").Diagnostic|undefined;
    let code = "REQUEST_FAILED";
    let message = `Control Center API returned HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string;diagnostic?:import("./diagnosticMessage").Diagnostic } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
	  diagnostic=body.error?.diagnostic;
    } catch {
      // Keep the stable fallback without exposing an upstream response body.
    }
    const requestId = response.headers.get("X-Request-ID") || "";
    throw new APIError(response.status, code, message,diagnostic,/^[A-Za-z0-9._-]{1,128}$/.test(requestId) ? requestId : "");
  }
  return (await response.json()) as T;
}

// 新增组件管理页复用同源会话、CSRF 和错误处理，不另建浏览器认证通道。
export { request as requestJSON };
